import React, { useState } from 'react';
import { toast } from './ui/use-toast';
import { RandomCompaniesProps, RandomCompany, QueryGroup } from '../types/randomCompanies';
import { parseInput } from '../utils/companyParsing';
import { useRandomCompanies } from '../hooks/useRandomCompanies';
import { QueryGroups } from './QueryGroups';
import { BatchVoteDialog } from './BatchVoteDialog';
import { ParticleEffects } from './ParticleEffects';
import { RandomCompaniesHeader } from './RandomCompaniesHeader';
import { RandomCompaniesSearch } from './RandomCompaniesSearch';
import { RandomCompaniesProgress } from './RandomCompaniesProgress';
import { RandomCompaniesGrid } from './RandomCompaniesGrid';
import { fetchFromScrapeRaw, fetchFromRandomCompanies } from '../services/companyDataService';

export const RandomCompanies: React.FC<RandomCompaniesProps> = ({ onAddCompany }) => {
  const {
    companies,
    queryGroups,
    addCompanies,
    addQueryGroup,
    removeGroup,
    clearAll,
    voteOnCompany,
    toggleExpand,
    closeOverlay,
    editCompany
  } = useRandomCompanies();

  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState('');
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchCompanies, setBatchCompanies] = useState<string[]>([]);
  const [batchType, setBatchType] = useState<'good' | 'bad'>('good');
  const [showParticles, setShowParticles] = useState(false);
  const [particlePosition, setParticlePosition] = useState({ x: 0, y: 0 });
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);

  const handleBatchVote = () => {
    batchCompanies.forEach(companyName => {
      onAddCompany(companyName, batchType);
    });

    toast({
      title: `Added ${batchCompanies.length} companies`,
      description: `All companies marked as ${batchType} examples.`,
    });

    setShowBatchDialog(false);
    setBatchCompanies([]);
    setInput('');
  };

  const performSearch = async (searchInput: string, isRefresh: boolean = false) => {
    if (!searchInput.trim()) {
      toast({
        title: "Input required",
        description: "Please enter industry keywords, company names, or website URLs.",
        variant: "destructive"
      });
      return;
    }

    const { domains, keywords, companyList } = parseInput(searchInput.trim());

    if (companyList.length > 0) {
      setBatchCompanies(companyList);
      setShowBatchDialog(true);
      return;
    }

    setIsLoading(true);
    
    try {
      let newCompanies: RandomCompany[] = [];

      if (domains.length > 0) {
        const scrapedCompanies = await fetchFromScrapeRaw(domains);
        newCompanies = [...newCompanies, ...scrapedCompanies];
      }

      if (keywords.trim()) {
        const randomCompanies = await fetchFromRandomCompanies(keywords);
        newCompanies = [...newCompanies, ...randomCompanies];
      }

      const existingDomains = new Set(companies.map(c => c.domain || c.name));
      const uniqueNewCompanies = newCompanies.filter(company => 
        !existingDomains.has(company.domain || company.name)
      );

      if (uniqueNewCompanies.length > 0) {
        const groupId = isRefresh ? 
          queryGroups.find(g => g.query === searchInput.trim())?.id || `group-${Date.now()}` :
          `group-${Date.now()}`;
        
        const newGroup: QueryGroup = {
          id: groupId,
          query: searchInput.trim(),
          timestamp: Date.now(),
          companyIds: uniqueNewCompanies.map(c => c.id)
        };

        const companiesWithGroup = uniqueNewCompanies.map(company => ({
          ...company,
          groupId
        }));

        addCompanies(companiesWithGroup, groupId);
        
        if (!isRefresh) {
          addQueryGroup(newGroup);
        } else {
          // Update existing group with new company IDs
          const existingGroup = queryGroups.find(g => g.id === groupId);
          if (existingGroup) {
            const updatedGroup = {
              ...existingGroup,
              companyIds: [...existingGroup.companyIds, ...uniqueNewCompanies.map(c => c.id)]
            };
            // Note: We'd need to add an updateQueryGroup function to the hook
          }
        }
        
        toast({
          title: isRefresh ? "More companies added" : "Companies added",
          description: `Added ${uniqueNewCompanies.length} new companies to your discovery list.`,
        });
      } else if (newCompanies.length > 0) {
        toast({
          title: "No new companies",
          description: "All companies from this search were already in your list.",
        });
      } else {
        toast({
          title: "No companies found",
          description: "Try different keywords or check the URLs you entered.",
        });
      }

      if (!isRefresh) {
        setInput('');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast({
        title: "Error",
        description: "Failed to fetch companies. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    await performSearch(input);
  };

  const handleSearchAgain = async (query: string) => {
    await performSearch(query, true);
  };

  const handleUndoGroup = (groupId: string) => {
    const group = queryGroups.find(g => g.id === groupId);
    if (!group) return;

    removeGroup(groupId);

    toast({
      title: "Query undone",
      description: `Removed ${group.companyIds.length} companies from "${group.query}".`,
    });
  };

  const handleClearAll = () => {
    clearAll();
    toast({
      title: "Cleared",
      description: "All companies and query groups have been removed.",
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleVote = (companyId: string, companyName: string, vote: 'good' | 'bad', event?: React.MouseEvent) => {
    // Trigger particle effects at mouse position
    if (event) {
      setParticlePosition({ x: event.clientX, y: event.clientY });
      setShowParticles(true);
    }

    voteOnCompany(companyId, vote);

    const success = onAddCompany(companyName, vote);
    
    if (success) {
      toast({
        title: vote === 'good' ? "Added as Good Example" : "Added as Bad Example",
        description: `${companyName} has been added to your ${vote} examples.`,
      });
    }
  };

  const handleExpand = (companyId: string) => {
    toggleExpand(companyId);
  };

  const handleEdit = (companyId: string) => {
    editCompany(companyId);
  };

  const votedCount = companies.filter(c => c.voted).length;

  // Separate voted and unvoted companies
  const unvotedCompanies = companies.filter(c => !c.voted);
  const votedCompanies = companies.filter(c => c.voted);

  return (
    <>
      <div className="space-y-4">
        <RandomCompaniesHeader 
          hasCompanies={companies.length > 0}
          onClearAll={handleClearAll}
        />

        <RandomCompaniesSearch
          input={input}
          isLoading={isLoading}
          onInputChange={setInput}
          onKeyDown={handleKeyDown}
          onSearch={handleSearch}
        />

        <QueryGroups 
          queryGroups={queryGroups}
          companies={companies}
          onUndoGroup={handleUndoGroup}
          onSearchAgain={handleSearchAgain}
          onGroupHover={setHoveredGroupId}
        />

        <RandomCompaniesProgress
          totalCompanies={companies.length}
          votedCount={votedCount}
        />

        <RandomCompaniesGrid
          unvotedCompanies={unvotedCompanies}
          votedCompanies={votedCompanies}
          onVote={handleVote}
          hoveredGroupId={hoveredGroupId}
          onExpand={handleExpand}
          onCloseOverlay={closeOverlay}
          onEdit={handleEdit}
        />

        {/* Help text */}
        {companies.length === 0 && (
          <div className="text-center text-slate-400 text-sm">
            Enter industry keywords (e.g., "ecommerce fashion"), company names, website URLs, or paste a large list of companies above to discover companies and vote on whether they're good or bad examples for your ICP. Your progress will be saved automatically.
          </div>
        )}
      </div>

      <BatchVoteDialog
        isOpen={showBatchDialog}
        onClose={() => setShowBatchDialog(false)}
        companies={batchCompanies}
        batchType={batchType}
        onBatchTypeChange={setBatchType}
        onConfirm={handleBatchVote}
      />

      <ParticleEffects
        x={particlePosition.x}
        y={particlePosition.y}
        trigger={showParticles}
        onComplete={() => setShowParticles(false)}
      />
    </>
  );
}; 